import { useEffect } from 'react';
import { Link, usePathParam } from 'react-resource-router';
import { GroupNode } from 'constants/types';
import { css } from '@compiled/react';
import {
  CardProps,
  CardContainerProps,
  UserGroup,
} from './GroupsFullListTypes';
import { UNIFIED_FORUMS_GROUPS_QUERY } from 'graphql/UnifiedForumsGroupsQuery';
import { UnifiedForumsGroupsQuery } from 'graphql/__generated__/UnifiedForumsGroupsQuery.graphql';
import { useLazyLoadQuery } from 'react-relay';
import { PROFILE_HEADER_QUERY } from 'graphql/ProfileHeaderQuery';
import { ProfileHeaderQuery } from 'graphql/__generated__/ProfileHeaderQuery.graphql';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
import { token } from '@atlaskit/tokens';
import { Groups } from '../../../assets/images';
import { MOBILE, DESKTOP } from '../../../constants/breakpoints';
import {
  trackGroupCardClick,
  trackGroupsBackToProfileClick,
} from 'analytics/groupsAnalytics';
import useIsPersonalView from 'utils/useIsPersonalView';
import { DEFAULT_FORUMS_GROUP_IMG_URL, SCREEN_NAME } from 'constants/index';
import {
  UnifiedAccountTagLoggedIn,
  UnifiedAccountTagLinked,
} from '@atlassian/learning-components';
import useHasLinkedAccounts from 'utils/useHasLinkedAccounts';
import { useSendScreenEventEffect } from 'hooks/useScreenEvent';
import { trackGroupsScreenView } from 'analytics/profileAnalytics';
import PrivateProfileComponent from '../private-profile/Wrapper';

const { REACT_APP_COMMUNITY_BASE_URL: COMMUNITY_BASE_URL } = process.env;

// Could use ElevatedCard here, but the padding in the Figma is different
// from a normal ElevatedCard

const Card = ({
  title,
  members,
  viewHref,
  topicsCount,
  avatarUrl,
  isPersonalView,
  testId,
}: CardProps) => {
  const onClickGroup = (groupLink: string, groupName?: string | null) => {
    trackGroupCardClick(
      groupName ?? 'not-named',
      isPersonalView,
      SCREEN_NAME.GROUPS,
    );
    window.open(groupLink);
  };
  return (
    <div data-testid={testId} css={styles.card}>
      <div css={styles.cardContent}>
        <div css={styles.header}>
          <img src={Groups} css={styles.icon} alt="Group icon" />
          <div css={styles.title}>{title}</div>
        </div>
        <div css={styles.imagePlaceholder}>
          {avatarUrl ? (
            <img
              src={`${COMMUNITY_BASE_URL}${avatarUrl}`}
              alt={`${title} image`}
              css={styles.image}
            />
          ) : null}
        </div>
      </div>
      <div css={styles.details}>
        <div css={styles.infoContainer}>
          <h6 css={styles.digit}>{members}</h6>
          <span css={styles.description}>{'members'}</span>
          <h6 css={styles.digit}>{topicsCount}</h6>
          <span css={styles.description}>{'posts'}</span>
        </div>
        <a
          onClick={() =>
            onClickGroup(`${COMMUNITY_BASE_URL}${viewHref}`, title)
          }
          css={styles.link}
        >
          {'View'}
        </a>
      </div>
    </div>
  );
};
const CardContainer = ({ aaid }: CardContainerProps) => {
  const [pathProfileId] = usePathParam('pathProfileId');

  if (!pathProfileId) {
    return null;
  }

  return <CardContainerContent aaid={aaid} pathProfileId={pathProfileId} />;
};

const CardContainerContent = ({
  aaid,
  pathProfileId,
}: CardContainerProps & { pathProfileId: string }) => {
  const hasLinkedAccounts = useHasLinkedAccounts();
  const unifiedGroupsResponse = useLazyLoadQuery<UnifiedForumsGroupsQuery>(
    UNIFIED_FORUMS_GROUPS_QUERY,
    { internalId: pathProfileId },
    { fetchPolicy: 'store-or-network' },
  );
  const isPersonalView = useIsPersonalView(pathProfileId);
  const filteredGroups = (
    unifiedGroupsResponse?.unified?.unifiedProfile?.forums?.groups?.edges ?? []
  ).map((edge) => edge?.node);
  const loggedInUserGroups: UserGroup[] = [];
  const linkedUserGroups: UserGroup[] = [];

  filteredGroups.forEach((group) => {
    if (group && group.aaid) {
      if (group.aaid === aaid) {
        loggedInUserGroups.push(group);
      } else {
        linkedUserGroups.push(group);
      }
    }
  });
  const loggedInUserGroupsCount = loggedInUserGroups.length;
  const linkedUserGroupsCount = linkedUserGroups.length;
  const loggedInHeadingTitle =
    loggedInUserGroupsCount === 1
      ? `${loggedInUserGroupsCount} Group`
      : `${loggedInUserGroupsCount} Groups`;
  const linkedHeadingTitle =
    linkedUserGroupsCount === 1
      ? `${linkedUserGroupsCount} Group`
      : `${linkedUserGroupsCount} Groups`;
  return (
    <div
      css={[
        styles.mainContainer,
        hasLinkedAccounts && styles.mainContainerTopBorder,
      ]}
    >
      <div css={styles.headingContainer}>
        <div css={styles.headingText}>{loggedInHeadingTitle}</div>
        {isPersonalView && hasLinkedAccounts && <UnifiedAccountTagLoggedIn />}
      </div>
      <div data-testid="groups-full-list-container" css={styles.container}>
        {loggedInUserGroups.map((group: GroupNode, index: number) => (
          <div key={index} css={styles.cardWrapper}>
            <Card
              testId={`groups-card-${group?.title || 'no-title'}`}
              title={group?.title || ''}
              members={group?.groupMemberCount || 0}
              viewHref={group?.viewHref || '#'}
              topicsCount={group?.topicsCount || 0}
              avatarUrl={getGroupImgUrl(group?.avatar?.mediumHref)}
              isPersonalView={isPersonalView}
            />
          </div>
        ))}
      </div>
      {linkedUserGroups.length > 0 && (
        <>
          <div css={styles.spacerTop} />
          <div css={styles.spacerBottom} />
          <div css={styles.headingContainer}>
            <div css={styles.headingText}>{linkedHeadingTitle}</div>
            <UnifiedAccountTagLinked />
          </div>
          <div data-testid="groups-full-list-container" css={styles.container}>
            {linkedUserGroups.map((group: GroupNode, index: number) => (
              <div key={index} css={styles.cardWrapper}>
                <Card
                  testId={`groups-card-${group?.title || 'no-title'}`}
                  title={group?.title || ''}
                  members={group?.groupMemberCount || 0}
                  viewHref={group?.viewHref || '#'}
                  topicsCount={group?.topicsCount || 0}
                  avatarUrl={getGroupImgUrl(group?.avatar?.mediumHref)}
                  isPersonalView={isPersonalView}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
const GroupsFullList = () => {
  const [pathProfileId] = usePathParam('pathProfileId');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!pathProfileId) {
    return null;
  }

  return <GroupsFullListContent pathProfileId={pathProfileId} />;
};

const GroupsFullListContent = ({
  pathProfileId,
}: {
  pathProfileId: string;
}) => {
  const hasLinkedAccounts = useHasLinkedAccounts();
  const isPersonalView = useIsPersonalView(pathProfileId);

  useSendScreenEventEffect(() => trackGroupsScreenView(isPersonalView), {
    shouldSendEvent: true,
  });

  const profileResponseData = useLazyLoadQuery<ProfileHeaderQuery>(
    PROFILE_HEADER_QUERY,
    { internalId: pathProfileId },
    { fetchPolicy: 'store-or-network' },
  );
  const profileData = profileResponseData?.unified?.unifiedProfile;
  const userResponseData = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
    ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
    { accountId: profileData?.aaid as string },
    { fetchPolicy: 'store-or-network' },
  );
  const userData = userResponseData?.user;

  const mainHeading = userData?.nickname
    ? `${userData.nickname}'s groups`
    : 'User groups';

  const onClickBackToProfile = () => {
    trackGroupsBackToProfileClick(isPersonalView);
  };

  return (
    <PrivateProfileComponent>
      <div css={styles.groupsFullList}>
        <div css={styles.contentWrapper}>
          <Link
            to={`profile/${pathProfileId}`}
            onClick={onClickBackToProfile}
            css={styles.backLink}
          >
            {'← Back to profile'}
          </Link>
          <div css={styles.mainHeading}>{mainHeading}</div>
          {isPersonalView && hasLinkedAccounts && (
            <div css={styles.subHeadingStyle}>
              {
                'To participate in a group from a linked account, log in to that account. Learn about '
              }
              <a href="https://www.atlassian.com" css={styles.linkText}>
                {'linking accounts.'}
              </a>
            </div>
          )}
          <CardContainer aaid={profileData?.aaid ?? ''} />
        </div>
      </div>
    </PrivateProfileComponent>
  );
};

const getGroupImgUrl = (url?: string | null): string =>
  url || DEFAULT_FORUMS_GROUP_IMG_URL;

const styles = {
  linkText: css({
    color: token('color.text.brand'),
    textDecoration: 'none',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: token('font.weight.regular'),
  }),
  spacerTop: css({
    height: '41px',
    borderBottom: `1px solid ${token('color.border')}`,
  }),
  spacerBottom: css({
    height: '40px',
  }),
  subHeadingStyle: css({
    fontFamily: 'Inter',
    fontStyle: 'normal',
    textTransform: 'none',
    fontWeight: token('font.weight.regular'),
    fontSize: '16px',
    alignSelf: 'flex-start',
    marginBottom: token('space.300'),
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        width: '720px',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      width: '343px',
    },
  }),
  card: css({
    borderRadius: '3px',
    padding: '16px 16px 24px 16px',
    width: '324px',
    minHeight: '262px',
    textAlign: 'center',
    // tokens not being used in figma for boxShadow and background
    boxShadow:
      '0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)',
    background: 'var(--neutral-light-n-0, #FFF)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        width: '348px',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      width: '343px',
    },
  }),
  cardContent: css({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  }),
  container: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: token('space.300'),
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      gridTemplateColumns: '1fr',
      gap: token('space.200'),
    },
  }),
  cardWrapper: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  header: css({
    display: 'flex',
    alignItems: 'top',
    justifyContent: 'left',
  }),
  icon: css({
    width: token('space.300'),
    height: token('space.300'),
    backgroundColor: 'lightgray',
    marginRight: token('space.100'),
  }),
  title: css({
    fontSize: token('space.200'),
    margin: '0',
    fontWeight: token('font.weight.semibold'),
    lineHeight: token('space.300'),
    textAlign: 'left',
    fontFamily: token('font.family.brand.heading'),
  }),
  imagePlaceholder: css({
    width: '100%',
    height: '150px',
    backgroundColor: 'white',
    margin: '16px 0',
    objectFit: 'cover',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    border: `1px solid var(--neutral-light-n-20, #F4F5F7)`,
  }),
  image: css({
    width: '100%',
    height: '100%',
  }),
  details: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
  }),
  infoContainer: css({
    display: 'flex',
    alignItems: 'baseline',
  }),
  digit: css({
    color: '#172B4D',
    fontSize: token('space.200'),
    fontWeight: token('font.weight.semibold'),
    lineHeight: token('space.300'),
    margin: '0 3px 0 0',
    fontFamily: token('font.family.brand.heading'),
  }),
  description: css({
    color: token('color.text.subtlest'),
    fontSize: '14px',
    fontWeight: token('font.weight.regular'),
    lineHeight: token('space.250'),
    marginRight: '7px',
    fontFamily: 'Inter',
  }),
  link: css({
    cursor: 'pointer',
    color: token('color.text.brand'),
    fontFamily: token('font.family.brand.body'),
    fontSize: token('space.200'),
    fontWeight: token('font.weight.semibold'),
    lineHeight: token('space.250'),
  }),
  groupsFullList: css({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: token('space.500'),
  }),
  contentWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '1020px',
    maxWidth: '100%',
    marginTop: '40px',
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        width: '720px',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      marginTop: '32px',
      width: '343px',
    },
  }),
  backLink: css({
    alignSelf: 'flex-start',
    color: token('color.text.brand'),
    fontFamily: token('font.family.brand.body'),
    fontSize: token('space.200'),
    fontWeight: token('font.weight.semibold'),
    lineHeight: token('space.250'),
  }),
  mainContainer: css({
    width: '100%',
    paddingTop: token('space.500'),
    padddingBottom: token('space.500'),
    marginBottom: token('space.500'),
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      paddingTop: token('space.300'),
      paddingBottom: token('space.300'),
    },
  }),
  mainContainerTopBorder: css({
    borderTop: `1px solid ${token('color.border')}`,
  }),

  headingContainer: css({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: token('space.300'),
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      marginBottom: token('space.200'),
    },
  }),
  headingText: css({
    fontSize: token('space.400'),
    color: token('color.text'),
    lineHeight: token('space.500'),
    fontWeight: token('font.weight.semibold'),
    fontFamily: token('font.family.brand.heading'),
    marginRight: '12px',
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      fontSize: token('space.300'),
      lineHeight: token('space.400'),
    },
  }),
  mainHeading: css({
    alignSelf: 'flex-start',
    width: '720px',
    marginTop: token('space.500'),
    marginBottom: token('space.300'),
    color: token('color.text'),
    fontFamily: token('font.family.brand.heading'),
    fontSize: token('space.500'),
    lineHeight: token('space.600'),
    fontWeight: token('font.weight.bold'),
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        width: '720px',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      width: '343px',
      marginTop: token('space.300'),
      marginBottom: token('space.300'),
      fontSize: token('space.400'),
      lineHeight: token('space.500'),
    },
  }),
};
export default GroupsFullList;
